import { z } from "zod";
import { fetcher } from "@/lib/fetcher";

function getApiErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== "object") return;
  const record = data as Record<string, unknown>;

  if (typeof record.message === "string" && record.message.trim() !== "") {
    return record.message;
  }
  if (typeof record.error === "string" && record.error.trim() !== "") {
    return record.error;
  }
  if (
    typeof record.error_code === "string" &&
    record.error_code.trim() !== ""
  ) {
    return record.error_code;
  }

  return;
}

export async function swrFetcher<T>(
  url: string,
  schema: z.ZodSchema<T>,
  init?: Parameters<typeof fetcher>[1],
): Promise<T> {
  // Use the shared fetcher so SWR requests include auth cookies + workspace headers.
  // `init` lets callers issue non-GET reads (e.g. QUERY with a body filter);
  // omitted → plain GET, preserving every existing caller's behavior.
  const raw = await fetcher<unknown>(url, init);
  const parsed = schema.parse(raw);

  // If the response has a top-level `status`, treat known non-success values as errors.
  if (parsed && typeof parsed === "object" && "status" in parsed) {
    const status = (parsed as Record<string, unknown>).status;
    if (typeof status === "string") {
      const normalized = status.toLowerCase();
      const okStatuses = new Set(["ok", "success", "warning", "info"]);
      const errorStatuses = new Set(["error", "unauthorized", "forbidden"]);

      if (errorStatuses.has(normalized)) {
        throw new Error(
          getApiErrorMessage(parsed) || `API request failed (status: ${status})`,
        );
      }

      // Unknown status values are allowed through (some endpoints may not follow the OK/ERROR convention).
      if (!okStatuses.has(normalized) && normalized !== "error") {
        // no-op
      }
    }
  }

  return parsed;
}
